import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Button, Alert, Typography, Tag, Space, Select, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCommentSms, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import caseService from '../../../services/CaseService';
import EmailTemplateService from '../../../services/EmailTemplateService';
import EmailBodyEditor from '../../EmailCampaigns/Builder/EmailBodyEditor';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// Personalization variables available in outreach messages (must match the
// backend OUTREACH_VARIABLES list).
const OUTREACH_VARIABLES = [
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'fullName', label: 'Full name' },
  { key: 'eventName', label: 'Event name' }
];

const stripHtml = (html = '') => String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();

// Must match the backend MAX_OUTREACH_RECIPIENTS cap.
const MAX_RECIPIENTS = 100;

/** Turn an estimated seconds count into a friendly phrase. */
const formatEta = (seconds) => {
  if (!seconds || seconds < 45) return 'under a minute';
  const mins = Math.round(seconds / 60);
  return `about ${mins} minute${mins === 1 ? '' : 's'}`;
};

/**
 * Compose and send email or text outreach to a set of members.
 * Email uses a rich HTML editor with personalization variables; sends are
 * throttled in the background, so we show an estimated duration.
 */
const ComposeOutreachModal = ({
  visible,
  method = 'email',
  recipients = [],
  eventKey,
  eventName,
  onClose,
  onSent
}) => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [emailBody, setEmailBody] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const isEmail = method === 'email';

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setEmailBody('');
      setSelectedTemplateId(null);
    }
  }, [visible, method, form]);

  // Load saved email templates once (email outreach only) so staff can start
  // from an existing design instead of writing every message from scratch.
  useEffect(() => {
    if (visible && isEmail && templates.length === 0) {
      EmailTemplateService.listTemplates({ limit: 100 }).then((res) => {
        if (res.success) setTemplates(res.data?.templates || []);
      });
    }
  }, [visible, isEmail, templates.length]);

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId || null);
    if (!templateId) return;
    const tpl = templates.find((t) => t._id === templateId);
    if (!tpl) return;
    form.setFieldsValue({ subject: tpl.subject || '' });
    setEmailBody(tpl.html || '');
    message.info('Template applied — subject and body updated');
  };

  const reachable = useMemo(
    () => recipients.filter((r) => (isEmail ? r.email : r.phone)),
    [recipients, isEmail]
  );
  const skipped = useMemo(
    () => recipients.filter((r) => (isEmail ? !r.email : !r.phone)),
    [recipients, isEmail]
  );
  const overLimit = recipients.length > MAX_RECIPIENTS;

  const handleSend = async () => {
    try {
      if (overLimit) {
        message.warning(`You can send to at most ${MAX_RECIPIENTS} members at a time.`);
        return;
      }
      const values = await form.validateFields();
      if (reachable.length === 0) {
        message.warning(`None of the selected members have ${isEmail ? 'an email' : 'a phone'} on file.`);
        return;
      }
      if (isEmail && !stripHtml(emailBody)) {
        message.warning('Please write a message.');
        return;
      }

      setSending(true);
      const res = await caseService.sendOutreach({
        method,
        eventKey,
        eventName,
        patientIds: recipients.map((r) => r.patientId),
        subject: isEmail ? values.subject : undefined,
        message: isEmail ? emailBody : values.message,
        isHtml: isEmail
      });

      const queued = res.queued ?? 0;
      const eta = formatEta(res.estimatedSeconds);
      if (queued > 0) {
        message.success(
          `Sending to ${queued} member${queued === 1 ? '' : 's'} — this will take ${eta}. You can keep working; progress shows in the Activity tab.`,
          6
        );
      } else {
        message.info('No reachable members to send to.');
      }
      if (res.skippedNoContact > 0) {
        message.info(`${res.skippedNoContact} skipped (missing contact info).`);
      }

      onSent?.();
      onClose?.();
    } catch (err) {
      if (err?.errorFields) return; // validation error, already shown
      message.error('Failed to start outreach');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={sending ? undefined : onClose}
      width={isEmail ? 760 : 620}
      title={
        <Space>
          <FontAwesomeIcon
            icon={isEmail ? faEnvelope : faCommentSms}
            style={{ color: COLORS.$primaryBlue }}
          />
          {isEmail ? 'Email Outreach' : 'Text Outreach'}
          {eventName && (
            <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
              · {eventName}
            </Text>
          )}
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} disabled={sending}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          loading={sending}
          onClick={handleSend}
          disabled={overLimit}
          icon={<FontAwesomeIcon icon={faPaperPlane} />}
        >
          Send to {reachable.length}
        </Button>
      ]}
    >
      <Paragraph style={{ marginBottom: 12 }}>
        <Text strong>{recipients.length}</Text> member{recipients.length === 1 ? '' : 's'} selected
        {reachable.length !== recipients.length && (
          <> · <Text strong>{reachable.length}</Text> reachable by {isEmail ? 'email' : 'text'}</>
        )}
      </Paragraph>

      {overLimit && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Too many recipients (${recipients.length})`}
          description={`You can send to at most ${MAX_RECIPIENTS} members at a time. Deselect some, or use event-based outreach in multiple passes for larger groups.`}
        />
      )}

      {skipped.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${skipped.length} member${skipped.length === 1 ? '' : 's'} will be skipped (no ${isEmail ? 'email' : 'phone'} on file)`}
          description={
            <Space size={[4, 4]} wrap>
              {skipped.slice(0, 8).map((r) => (
                <Tag key={r.patientId}>{r.name}</Tag>
              ))}
              {skipped.length > 8 && <Tag>+{skipped.length - 8} more</Tag>}
            </Space>
          }
        />
      )}

      <Form form={form} layout="vertical">
        {isEmail ? (
          <>
            <Form.Item
              label="Start from a template"
              extra="Optional — pick a saved email template to prefill the subject and body."
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                value={selectedTemplateId}
                onChange={applyTemplate}
                placeholder={templates.length ? 'Choose a saved template…' : 'No saved templates yet'}
                disabled={sending || templates.length === 0}
                options={templates.map((t) => ({ value: t._id, label: t.name }))}
                notFoundContent="No templates found"
              />
            </Form.Item>
            <Form.Item
              name="subject"
              label="Subject"
              rules={[{ required: true, message: 'Please enter a subject' }]}
              extra="Tip: personalize with variables, e.g. Following up, {{firstName}}"
            >
              <Input placeholder="e.g. Following up on your recent heart-health screening" />
            </Form.Item>
            <EmailBodyEditor
              value={emailBody}
              onChange={setEmailBody}
              disabled={sending}
              variables={OUTREACH_VARIABLES}
            />
          </>
        ) : (
          <Form.Item
            name="message"
            label="Text message"
            rules={[{ required: true, message: 'Please enter a message' }]}
            extra="Keep it short — long texts may be split into segments. Personalize with {{firstName}}, {{eventName}}."
          >
            <TextArea
              rows={4}
              maxLength={1000}
              showCount
              placeholder="Hi {{firstName}}, this is the Black Heart Association following up on your recent screening..."
            />
          </Form.Item>
        )}
      </Form>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 12 }}
        message="Sent gradually to protect deliverability"
        description={`Messages go out in small batches over ${formatEta(
          Math.max(1, reachable.length) * (isEmail ? 1.2 : 0.6)
        )} rather than all at once.${isEmail ? ' Invalid email addresses are checked and skipped automatically.' : ''}`}
      />
    </Modal>
  );
};

export default ComposeOutreachModal;
